import {
  ContentCopyOutlined,
  Send,
  ThumbDownOffAlt,
  ThumbUpOffAlt,
  VerifiedOutlined,
} from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Popover, Select, TextField, Typography } from "@mui/material";
import { useToast } from "@/hooks/use-toast";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { v4 as uuid } from "uuid";
import { bindActionCreators } from "redux";
import { BotChatMessage } from "../../../../models/botChatMessage";
import { BotConversation } from "../../../../models/botConversation";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import { Dispatch } from "../../../../store";
import { getConversationsAction } from "../../../../store/Conversation/actions";
import { FlexContainer } from "../../../../styles/FlexContainer";
import { useAuth } from "../../../Reusable/AuthProvider";
import { ScrollToBottomContainer } from "../../../Reusable/ScrollToBottomContainer";
import { useCompletedMessages } from "../../../Reusable/UseCompletedMessages";
import { getUserFullName, useDateFormatting } from "../../../Reusable/Utils";
import RequestCertification from "./RequestCertification";
import { Certification } from "../../../../models/certification";
import { getStatusLabelKey } from "../../../../models/certificationStatus";
import CertificationConversation from "../Certifications/CertificationConversation";

const iconStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
};

const CERT_VISUALS: Record<string, { emoji: string; color: string }> = {
  CERTIFIED: { emoji: "✅", color: "#4caf50" },
  CERTIFIED_WITH_RESERVATIONS: { emoji: "⚠️", color: "#ff9800" },
  CORRECTED: { emoji: "✏️", color: "#2196f3" },
  DECLINED: { emoji: "❌", color: "#f44336" },
  CANCELLED: { emoji: "🚫", color: "#9e9e9e" },
  PENDING: { emoji: "⏳", color: "#9e9e9e" },
  IN_REVIEW: { emoji: "⏳", color: "#9e9e9e" },
  WAITING_FOR_USER: { emoji: "⏳", color: "#9e9e9e" },
};

const certVisual = (status?: string) =>
  (status && CERT_VISUALS[status]) || { emoji: "⏳", color: "#9e9e9e" };

const ThinkingIndicator: React.FC<{ steps: string[] }> = ({ steps }) => {
  const completedSteps = steps.length > 1 ? steps.slice(0, -1) : [];
  const currentStep = steps.length > 0 ? steps[steps.length - 1] : null;

  const bouncingDots = (
    <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            animationName: "thinkingDot",
            animationDuration: "1.4s",
            animationTimingFunction: "ease-in-out",
            animationIterationCount: "infinite",
            animationDelay: `${i * 0.22}s`,
            "@keyframes thinkingDot": {
              "0%, 60%, 100%": { transform: "scale(0.5)", opacity: 0.25 },
              "30%": { transform: "scale(1.15)", opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", py: 1 }}>
      {/* Completed steps */}
      {completedSteps.map((step, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.45 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 9, color: "#fff", lineHeight: 1 }}>✓</Typography>
          </Box>
          <Typography sx={{ fontSize: "0.82rem", textDecoration: "line-through", color: "hsl(var(--muted-foreground))" }}>
            {step}
          </Typography>
        </Box>
      ))}

      {/* Current step with bouncing dots, or initial "Réflexion..." */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {bouncingDots}
        <Typography sx={{ fontSize: "0.85rem", fontStyle: "italic", color: "hsl(var(--card-foreground))" }}>
          {currentStep ?? "Réflexion en cours..."}
        </Typography>
      </Box>

      {/* Indeterminate progress bar */}
      <LinearProgress
        sx={{
          borderRadius: 4,
          height: 3,
          backgroundColor: "hsl(var(--muted))",
          "& .MuiLinearProgress-bar": { borderRadius: 4 },
        }}
      />
    </Box>
  );
};

export interface ChatPageProps {
  getConversationsAction: typeof getConversationsAction;
}

const _ChatPage: React.FC<ChatPageProps> = ({ getConversationsAction }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormatting();
  const [messages, setMessages] = useState<BotChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<string[]>([]);
  const { markAsCompleted, isCompleted } = useCompletedMessages();
  const [popupCertifyActive, setPopupCertifyActive] = useState("");
  const [previewCert, setPreviewCert] = useState<Certification | null>(null);

  const openCertPreview = (certId: string) => {
    AxiosHttpClient.get<Certification>(
      "api/v1/docuveritas/conversations/messages/certification/" + certId
    )
      .then(setPreviewCert)
      .catch(console.error);
  };

  // Fiscal-year filter: the AI answer is based only on documents in force during the
  // selected year (matched against each document's validity window in the DB).
  // Defaults to the current year ("present moment").
  const currentYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState<number>(currentYear);
  const { toast } = useToast();
  const fiscalYearOptions = React.useMemo(
    () => Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i),
    [currentYear]
  );

  // State for source citation popover
  const [citeAnchor, setCiteAnchor] = useState<HTMLElement | null>(null);
  const [citeData, setCiteData] = useState<{
    n: string; docId: string; docTitle: string; content: string; chunkId: string;
  } | null>(null);

  const handleCiteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const cite = (e.target as HTMLElement).closest?.(".src-cite") as HTMLElement | null;
    if (!cite) return;
    setCiteAnchor(cite);
    setCiteData({
      n: cite.dataset.n || "",
      docId: cite.dataset.docId || "",
      docTitle: cite.dataset.docTitle || "",
      content: cite.dataset.content || "",
      chunkId: cite.dataset.chunkId || "",
    });
  };

  const params = useParams();
  const navigate = useNavigate();

  const justCreatedConversation = React.useRef(false);

  const isLastMessage = (index: number) => index === messages.length - 1;

  const isCompletedMessage = (message: BotChatMessage, index: number) => {
    if (message.fromUser) return false;
    if (!isLastMessage(index)) return true;
    return !isLoading && message.content.trim() !== "";
  };

  // Charger les messages existants
  const getChatMessages = () => {
    AxiosHttpClient.get<BotChatMessage[]>(
      "api/v1/docuveritas/conversations/messages/" + params.conversationId
    )
      .then(setMessages)
      .catch(console.error);
  };

  // Ajouter un message côté serveur (non IA)
  const addChatMessage = async (content: string) => {
    if (!user || !params.conversationId) return;
    const newMessage: BotChatMessage = {
      id: uuid(),
      content,
      fromUser: true,
      createdAt: null,
      documents: [],
      certifications: [],
      conversation: {
        id: params.conversationId,
        title: "",
        createdAt: null,
        user,
        messages: [],
      },
      question: null,
      response: null,
    };

    // Ajouter côté UI immédiatement
    setMessages((prev) => [...prev, newMessage]);

    // Enregistrer côté backend
    const response = await AxiosHttpClient.post<BotChatMessage>(
      "api/v1/docuveritas/conversations/messages/" + params.conversationId,
      newMessage
    );
    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const messageToSend = inputMessage.trim();
    if (!messageToSend || !user) return;

    setInputMessage("");
    setIsLoading(true);

    let conversationId = params.conversationId;

    let createdMessage: BotChatMessage | null | undefined = null;

    if (!conversationId) {
      // Pas encore de conversation, on en crée une
      try {
        const response = await AxiosHttpClient.post<BotConversation>(
          "api/v1/docuveritas/conversations",
          {
            message: messageToSend,
          }
        );
        createdMessage = response.messages[0];
        conversationId = response.id;
        justCreatedConversation.current = true;
        navigate(`/feature/chat/${conversationId}`, { replace: true });

        setMessages((prev) => [
          ...prev,
          {
            id: uuid(),
            content: messageToSend,
            fromUser: true,
            createdAt: null,
            documents: [],
            certifications: [],
            conversation: {
              id: conversationId!,
              title: "",
              createdAt: null,
              user,
              messages: [],
            },
            question: null,
            response: null,
          },
        ]);
        getConversationsAction();
      } catch (err) {
        console.error("Erreur création conversation", err);
        setIsLoading(false);
        return;
      }
    } else {
      // Cas normal : conversation existante
      createdMessage = await addChatMessage(messageToSend);
    }
    if (!createdMessage) {
      setIsLoading(false);
      return;
    }

    // IA message temporaire
    const botMessageId = uuid();
    setMessages((prev) => [
      ...prev,
      {
        id: botMessageId,
        content: "",
        fromUser: false,
        createdAt: null,
        documents: [],
        certifications: [],
        conversation: {
          id: conversationId!,
          title: "",
          createdAt: null,
          user,
          messages: [],
        },
        question: null,
        response: null,
      },
    ]);

    // Appel backend (fiscalYear restreint la réponse aux documents en vigueur cette année-là)
    const res = await fetch(
      AxiosHttpClient.baseUrl +
        "api/v1/docuveritas/conversations/messages/ai/" +
        createdMessage.id +
        "?fiscalYear=" +
        fiscalYear,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwtToken"),
        },
      }
    );

    if (!res.ok) {
      // 429 = per-user quota (Retry-After in seconds); anything else = generic failure
      const retryAfterMin = Math.ceil(Number(res.headers.get("Retry-After") || 0) / 60);
      toast({
        title: t("CHAT_PAGE_ERROR_TITLE"),
        description:
          res.status === 429
            ? t("CHAT_PAGE_QUOTA_REACHED", { minutes: Math.max(1, retryAfterMin) })
            : t("CHAT_PAGE_ANSWER_FAILED"),
        variant: "destructive",
      });
      setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
      setIsLoading(false);
      return;
    }

    if (!res.body) {
      console.error("Réponse vide du serveur");
      setIsLoading(false);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let done = false;
    let accumulated = "";
    let lineBuffer = "";
    let inAnswerMode = false;
    const currentSteps: string[] = [];
    setThinkingSteps([]);

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;

      const chunk = decoder.decode(value || new Uint8Array(), {
        stream: !done,
      });

      if (!inAnswerMode) {
        lineBuffer += chunk;
        let newlineIdx: number;
        while ((newlineIdx = lineBuffer.indexOf("\n")) !== -1) {
          const line = lineBuffer.slice(0, newlineIdx);
          lineBuffer = lineBuffer.slice(newlineIdx + 1);
          if (line.startsWith("__STEP__:")) {
            const stepText = line.slice("__STEP__:".length).trim();
            if (stepText) {
              currentSteps.push(stepText);
              setThinkingSteps([...currentSteps]);
            }
          } else if (line === "__ANSWER_START__") {
            inAnswerMode = true;
            if (lineBuffer) {
              accumulated += lineBuffer;
              lineBuffer = "";
            }
            break;
          }
        }
      } else {
        accumulated += chunk;
      }

      if (accumulated) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId ? { ...msg, content: accumulated } : msg
          )
        );
      }
    }

    if (done) {
      markAsCompleted(botMessageId);
      // Rafraîchir la liste des conversations pour afficher le titre généré par l'IA
      getConversationsAction();
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (justCreatedConversation.current) {
      justCreatedConversation.current = false; // Reset pour les prochains navigate
      return;
    }

    if (params.conversationId) {
      getChatMessages();
    } else {
      setMessages([]);
    }
  }, [params.conversationId]);

  useEffect(() => {
    if (!isLoading) {
      getChatMessages();
    }
  }, [isLoading]);

  return (
    <FlexContainer
      flexDirection="column"
      minHeight="100%"
      width="100%"
      alignItems={"center"}
      sx={{
        backgroundColor: "hsl(var(--background))",
      }}
    >
      {/* Zone des messages */}
      <ScrollToBottomContainer
        showButtonToBottom
        sx={{
          width: "100%",
          padding: "16px 16px 40px 16px",
        }}
      >
        <Box
          sx={{
            maxWidth: "54rem",
            margin: "0 auto",
            "& > * + *": { marginTop: "16px" },
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              <p>{t("CHAT_PAGE_START_CONVERSATION")}</p>
            </Box>
          ) : (
            messages.map((message, index) => {
              const cert = message.certifications[0];
              const visual = certVisual(cert?.status);
              const isThinking =
                !message.fromUser && message.content.trim() === "" && isLoading && isLastMessage(index);
              // The thinking indicator keeps its content width (centered by the FlexContainer);
              // only rendered answers span the full width so headings/tables have room.
              const isFullWidthAnswer = !message.fromUser && !isThinking;
              return (
                <FlexContainer
                  key={message.id}
                  sx={{
                    justifyContent: message.fromUser ? "flex-end" : "center",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: message.fromUser ? "85%" : isFullWidthAnswer ? "100%" : "95%",
                      width: isFullWidthAnswer ? "100%" : undefined,
                      borderRadius: message.fromUser ? "18px" : "8px",
                      padding: message.fromUser ? "12px 18px" : isFullWidthAnswer ? "8px 16px" : "16px",
                      ...(message.fromUser ? { whiteSpace: "pre-wrap" } : {}),
                      border: cert ? `2px solid ${visual.color}` : undefined,
                      ...(message.fromUser
                        ? {
                            backgroundColor: "hsl(var(--muted))",
                            color: "hsl(var(--card-foreground))",
                          }
                        : {
                            color: "hsl(var(--card-foreground))",
                            fontSize: "1rem",
                          }),
                    }}
                  >
                    {message.fromUser ? (
                      message.content
                    ) : isThinking ? (
                      <ThinkingIndicator steps={thinkingSteps} />
                    ) : (
                      <div
                        onClick={handleCiteClick}
                        className="ai-response"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }}
                      />
                    )}

                    {/* Zone de certification */}
                    {cert && (
                      <Box sx={{ mt: 2, p: 1, borderTop: "1px solid #ccc" }}>
                        <Box
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            mb: 0.5,
                            color: visual.color,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>{visual.emoji}</span>
                          <span>
                            {t(getStatusLabelKey(cert.status))}
                            {cert.certifiedBy
                              ? " " +
                                t("CHAT_PAGE_CERT_BY", {
                                  name: getUserFullName(cert.certifiedBy),
                                  date: formatDateTime(cert.certifiedAt),
                                })
                              : ""}
                          </span>
                        </Box>
                        {cert.finalDecision && (
                          <Box
                            sx={{
                              fontSize: "0.85rem",
                              color: "hsl(var(--muted-foreground))",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {cert.finalDecision}
                          </Box>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => openCertPreview(cert.id)}
                          sx={{
                            mt: 0.5,
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                          }}
                        >
                          {t("CHAT_PAGE_CERT_VIEW")}
                        </Button>
                      </Box>
                    )}

                    {!message.fromUser &&
                      isCompletedMessage(message, index) && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            marginTop: 2,
                            marginRight: 2,
                          }}
                        >
                          <Box
                            component="button"
                            onClick={() => {
                              navigator.clipboard.writeText(message.content);
                            }}
                            title={t("CHAT_PAGE_TOOLTIP_COPY")}
                            sx={{
                              ...iconStyle,
                              color: "hsl(var(--muted-foreground))",
                              "&:hover": { color: "hsl(var(--foreground))" },
                            }}
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </Box>
                          <Box
                            component="button"
                            onClick={() => {}}
                            title={t("CHAT_PAGE_TOOLTIP_LIKE")}
                            sx={{
                              ...iconStyle,
                              color: "hsl(var(--muted-foreground))",
                              "&:hover": { color: "hsl(var(--foreground))" },
                            }}
                          >
                            <ThumbUpOffAlt fontSize="small" />
                          </Box>
                          <Box
                            component="button"
                            onClick={() => {}}
                            title={t("CHAT_PAGE_TOOLTIP_DISLIKE")}
                            sx={{
                              ...iconStyle,
                              color: "hsl(var(--muted-foreground))",
                              "&:hover": { color: "hsl(var(--foreground))" },
                            }}
                          >
                            <ThumbDownOffAlt fontSize="small" />
                          </Box>
                          {!cert && (
                            <Box
                              component="button"
                              onClick={() => {
                                setPopupCertifyActive(message.id);
                              }}
                              title={t("CHAT_PAGE_TOOLTIP_CERTIFY")}
                              sx={{
                                ...iconStyle,
                                color: "hsl(var(--muted-foreground))",
                                "&:hover": { color: "hsl(var(--foreground))" },
                              }}
                            >
                              <VerifiedOutlined fontSize="small" />
                            </Box>
                          )}
                        </Box>
                      )}
                  </Box>
                </FlexContainer>
              );
            })
          )}
        </Box>
      </ScrollToBottomContainer>

      {/* Input Form */}
      <Box
        sx={{
          width: "90%",
          maxWidth: "750px",
          zIndex: 500,
          marginTop: "-24px",
        }}
      >
        <Box
          sx={{
            borderRadius: 6,
            boxShadow: 3,
            p: 2,
            width: "100%",
            backgroundColor: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              multiline
              maxRows={8}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={t("CHAT_PAGE_INPUT_PLACEHOLDER")}
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault(); // empêche retour à la ligne
                  handleSubmit(e); // déclenche l'envoi du message
                }
              }}
              sx={{
                width: "100%",
                padding: "8px",
                "& .MuiInputBase-input": {
                  color: "hsl(var(--foreground))",
                  "&::placeholder": {
                    color: "hsl(var(--muted-foreground))",
                    opacity: 1,
                  },
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  "&:hover .fy-tooltip": { opacity: 1, visibility: "visible" },
                }}
              >
                <Select
                  value={fiscalYear}
                  onChange={(e) => setFiscalYear(Number(e.target.value))}
                  size="small"
                  variant="outlined"
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  sx={{
                    fontSize: "0.8rem",
                    color: "hsl(var(--muted-foreground))",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(var(--border))" },
                    "& .MuiSvgIcon-root": { color: "hsl(var(--muted-foreground))" },
                    "& .MuiSelect-select": { py: 0.5, pl: 1.25, pr: 3 },
                  }}
                >
                  {fiscalYearOptions.map((y) => (
                    <MenuItem key={y} value={y} sx={{ fontSize: "0.8rem" }}>
                      {y === currentYear ? `${y} (actuelle)` : y}
                    </MenuItem>
                  ))}
                </Select>
                <Box
                  className="fy-tooltip"
                  sx={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: 0,
                    zIndex: 1300,
                    width: 250,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    lineHeight: 1.35,
                    backgroundColor: "hsl(var(--foreground))",
                    color: "hsl(var(--background))",
                    boxShadow: 3,
                    opacity: 0,
                    visibility: "hidden",
                    transition: "opacity 0.15s ease",
                    pointerEvents: "none",
                  }}
                >
                  Année fiscale : la réponse se base sur les documents en vigueur durant l'année sélectionnée
                </Box>
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={!inputMessage.trim()}
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  p: "10px 12px 12px 12px",
                  borderRadius: 2,
                  ":hover": { backgroundColor: "primary.dark" },
                  opacity: inputMessage.trim() ? 1 : 0.5,
                  cursor: inputMessage.trim() ? "pointer" : "not-allowed",
                  border: "none",
                }}
              >
                <Send fontSize={"small"} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          p: 1,
          fontSize: "0.8rem",
          textAlign: "center",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        {t("CHAT_PAGE_DISCLAIMER")}
      </Box>
      {popupCertifyActive && (
        <RequestCertification
          popupActive={popupCertifyActive}
          setPopupActive={setPopupCertifyActive}
          messages={messages}
        />
      )}

      {previewCert && (
        <Dialog
          open
          onClose={() => setPreviewCert(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t(getStatusLabelKey(previewCert.status))}</DialogTitle>
          <DialogContent dividers>
            <CertificationConversation certification={previewCert} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewCert(null)}>
              {t("CHAT_PAGE_CERT_CLOSE")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const id = previewCert.id;
                setPreviewCert(null);
                navigate(`/feature/certifications/details/${id}`);
              }}
            >
              {t("CHAT_PAGE_CERT_OPEN_DETAILS")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Source citation popover */}
      <Popover
        open={Boolean(citeAnchor)}
        anchorEl={citeAnchor}
        onClose={() => setCiteAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { maxWidth: 420, p: 2 } } }}
      >
        {citeData && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              [{citeData.n}] {citeData.docTitle}
            </Typography>
            {citeData.content && (
              <Box
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  mb: 1.5,
                  borderLeft: "3px solid #1976d2",
                  pl: 1,
                  fontSize: "0.875rem",
                  "& *": { margin: 0 },
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    citeData.content + (citeData.content.length >= 400 ? "…" : "")
                  ),
                }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setCiteAnchor(null);
                const url = `/feature/legalDocuments/pdf-viewer/${citeData.docId}${
                  citeData.chunkId ? `?chunk=${encodeURIComponent(citeData.chunkId)}` : ""
                }`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              Voir le document
            </Button>
          </>
        )}
      </Popover>
    </FlexContainer>
  );
};

export const ChatPage = connect(null, (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getConversationsAction: getConversationsAction,
    },
    dispatch
  )
)(_ChatPage);

export default ChatPage;
